import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Input, Select, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { LimitationType, LimitationValue } from '../../../../api-client';
import { limitationTypeTag } from '../../../../utils/enumUtils';

type BVLimitationsValueTableProps = {
	loading: boolean;
	values: LimitationValue[];
	selectedType: LimitationType;
	onTypeChange: (type: LimitationType) => void | Promise<void>;
	onRefresh: () => void | Promise<void>;
};

const typeTag = (t?: LimitationType) => {
	var tagInfo = limitationTypeTag(t);
	return <Tag color={tagInfo.color}>{tagInfo.typeString}</Tag>;
};

const BVLimitationsValueTable = ({
	loading,
	values,
	selectedType,
	onTypeChange,
	onRefresh,
}: BVLimitationsValueTableProps) => {
	const [search, setSearch] = useState<string>('');

	const typeOptions = useMemo(
		() => [
			{ label: 'SalesChannel', value: 'SalesChannel' },
			{ label: 'Customer', value: 'Customer' },
			{ label: 'Cluster', value: 'Cluster' },
			{ label: 'OperatingCountry', value: 'OperatingCountry' },
			{ label: 'ResponsabilityArea', value: 'ResponsabilityArea' },
		],
		[],
	);

	const isCustomer = selectedType === ('Customer' as LimitationType);

	const normalizedSearch = useMemo(() => search.trim().toLowerCase(), [search]);

	const filteredValues = useMemo(() => {
		if (!isCustomer) return values ?? [];
		if (!normalizedSearch) return values ?? [];

		return (values ?? []).filter((v) =>
			(v.value ?? '').toLowerCase().includes(normalizedSearch),
		);
	}, [values, isCustomer, normalizedSearch]);

	const copyToClipboard = async (text?: string) => {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
			message.success('Value copied');
		} catch {
			message.error('Copy failed');
		}
	};

	const columns: ColumnsType<LimitationValue> = useMemo(
		() => [
			{
				title: 'Type',
				dataIndex: 'type',
				key: 'type',
				width: 260,
				render: (v: LimitationType) => typeTag(v),
			},
			{
				title: 'Value',
				dataIndex: 'value',
				key: 'value',
				render: (v?: string) => v || '-',
			},
			{
				title: '',
				key: 'actions',
				width: 60,
				align: 'center',
				render: (_, record) => (
					<Button
						type="text"
						icon={<CopyOutlined />}
						disabled={!record.value}
						onClick={() => copyToClipboard(record.value)}
					/>
				),
			},
		],
		[],
	);

	const handleTypeChange = async (t: LimitationType) => {
		setSearch('');
		await onTypeChange(t);
	};

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<label className="admin-label">Limitation values</label>

				<div
					style={{
						display: 'flex',
						gap: 12,
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<Select
						style={{ minWidth: 280 }}
						value={selectedType}
						options={typeOptions}
						onChange={(v) => handleTypeChange(v as LimitationType)}
					/>
				</div>

				<div
					style={{
						display: 'flex',
						gap: 12,
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<div style={{ width: '200', display: 'flex' }}>
						<Input
							allowClear
							placeholder="Search customer value..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							style={{
								width: '100%',
								visibility: isCustomer ? 'visible' : 'hidden', // mantiene spazio, non si vede
								pointerEvents: isCustomer ? 'auto' : 'none', // non cliccabile quando hidden
							}}
						/>
					</div>

					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						loading={loading}
					>
						Refresh
					</Button>
				</div>
			</div>

			<Table<LimitationValue>
				style={{ marginTop: 12 }}
				rowKey={(r, idx) => `${r.type}-${r.value}-${idx}`}
				loading={loading}
				dataSource={filteredValues}
				columns={columns}
				pagination={{ pageSize: 20 }}
			/>
		</div>
	);
};

export default BVLimitationsValueTable;
